import type { MouseEvent } from 'react';
import type {
  TesterQueueResponse,
  TesterQueueJob,
  TesterQueueInProgressJob,
  TesterQueueLateSubmissionJob,
} from '@runhuman/shared';
import { formatTimeSinceCreated, openAdminJobDetail, openTestHarness } from './harness';
import { JobRow } from './JobRow';
import { ExternalLinkIcon } from '../../ExternalLinkIcon';

interface Props {
  readonly queue: TesterQueueResponse;
  readonly onClaim: (job: TesterQueueJob) => void;
  /**
   * Suppresses Claim while the viewer already has an active job. Independent
   * of `claimDisabledReason` — `disabled` is "you can't claim *more* right
   * now" (transient, no surface explanation needed), while
   * `claimDisabledReason` is "you can't claim *at all*" (#3919, persistent
   * with a tooltip).
   */
  readonly disabled?: boolean;
  /**
   * When set, the Claim button is rendered disabled with this reason on
   * `title`/aria so non-tester admins (#3919) see *why* the gate is
   * closed rather than a silently-no-op button.
   */
  readonly claimDisabledReason?: string | null;
  /**
   * When true, in-progress rows render an admin-only "View" button (#3922)
   * that opens `/admin/jobs/:jobId` on the web — same job detail surface an
   * admin would reach from the dashboard. Gated here (not on the server) so
   * the queue payload stays role-agnostic; the role flag flows from the
   * resolve response's `capabilities.isAdmin`.
   */
  readonly isAdmin?: boolean;
}

/**
 * Three-section queue surface — parity with `runhuman.com/tester` so the
 * Activity is never a strict subset of what the web Tester Portal shows:
 *
 *   • Available — qualification-filtered jobs the viewer can claim.
 *   • In progress — every job currently being worked on across the cohort
 *     (read-only signal: who's testing what right now).
 *   • Needs late submission — the viewer's claimed-but-abandoned rows that
 *     still have a usable testerToken; clicking opens the web harness so
 *     they can either ship the result or drop it via "Don't submit".
 *
 * Sections render only when populated, so an empty cohort doesn't pad the
 * iframe with three "no jobs" cards. The viewer's own active job is owned by
 * `<ActiveJobPanel>` (top of view), not by this component.
 *
 * Every section delegates its row layout to `<JobRow>` (#3924 + #3925) so
 * title/URL/expansion behavior is shared — the per-section variation lives
 * only in the trailing affordance (Claim button, tester alias, "Resume to
 * submit") and the time-badge source.
 */
export function QueueList({ queue, onClaim, disabled, claimDisabledReason, isAdmin }: Props) {
  const { available, inProgress, needsLateSubmission } = queue;

  if (available.length === 0 && inProgress.length === 0 && needsLateSubmission.length === 0) {
    return (
      <section className="queue queue--empty">
        <h2>Queue</h2>
        <p>No jobs in flight right now. Hang out — we'll ping you when one lands.</p>
      </section>
    );
  }

  return (
    <>
      {available.length > 0 && (
        <AvailableSection
          jobs={available}
          onClaim={onClaim}
          disabled={disabled}
          claimDisabledReason={claimDisabledReason}
        />
      )}
      {needsLateSubmission.length > 0 && (
        <LateSubmissionSection jobs={needsLateSubmission} />
      )}
      {inProgress.length > 0 && <InProgressSection jobs={inProgress} isAdmin={isAdmin} />}
    </>
  );
}

/**
 * Stops the row-level click handler from firing when the user clicks an
 * action button inside the trailing slot. Without this, clicking Claim
 * would also toggle the row's expansion state — JobRow uses the whole
 * `<li>` as a click target so the chevron isn't a tiny hit area.
 */
function stopRowToggle(event: MouseEvent<HTMLButtonElement>): void {
  event.stopPropagation();
}

interface AvailableSectionProps {
  readonly jobs: readonly TesterQueueJob[];
  readonly onClaim: (job: TesterQueueJob) => void;
  readonly disabled?: boolean;
  readonly claimDisabledReason?: string | null;
}

function AvailableSection({
  jobs,
  onClaim,
  disabled,
  claimDisabledReason,
}: AvailableSectionProps) {
  const cantClaim = Boolean(claimDisabledReason);
  const claimDisabled = disabled || cantClaim;
  return (
    <section className="queue">
      <h2>Available ({jobs.length})</h2>
      {cantClaim && (
        <p className="queue__cant-claim-banner" role="status">
          {claimDisabledReason}
        </p>
      )}
      <ul className="queue__list">
        {jobs.map((job) => (
          <JobRow
            key={job.id}
            job={job}
            timeBadge={formatTimeSinceCreated(job.createdAt)}
            trailing={
              <button
                type="button"
                className="queue__claim"
                onClick={(event) => {
                  stopRowToggle(event);
                  onClaim(job);
                }}
                disabled={claimDisabled}
                title={cantClaim ? claimDisabledReason ?? undefined : undefined}
                aria-label={cantClaim ? `Claim disabled — ${claimDisabledReason}` : undefined}
              >
                Claim
                <ExternalLinkIcon />
              </button>
            }
          />

        ))}
      </ul>
    </section>
  );
}

function InProgressSection({
  jobs,
  isAdmin,
}: {
  readonly jobs: readonly TesterQueueInProgressJob[];
  readonly isAdmin?: boolean;
}) {
  return (
    <section className="queue queue--in-progress">
      <h2>In progress ({jobs.length})</h2>
      <ul className="queue__list">
        {jobs.map((job) => (
          <JobRow
            key={job.id}
            job={job}
            variant="in-progress"
            timeBadge={formatTimeSinceCreated(job.claimedAt)}
            trailing={
              <>
                <span className="queue__tester">{job.testerAlias}</span>
                {isAdmin && (
                  <button
                    type="button"
                    className="queue__view"
                    onClick={(event) => {
                      stopRowToggle(event);
                      void openAdminJobDetail(job.id);
                    }}
                  >
                    View
                    <ExternalLinkIcon />
                  </button>
                )}
              </>
            }
          />
        ))}
      </ul>
    </section>
  );
}

function LateSubmissionSection({ jobs }: { readonly jobs: readonly TesterQueueLateSubmissionJob[] }) {
  return (
    <section className="queue queue--late">
      <h2>Needs submission ({jobs.length})</h2>
      <ul className="queue__list">
        {jobs.map((job) => (
          <JobRow
            key={job.id}
            job={job}
            timeBadge={formatTimeSinceCreated(job.claimedAt)}
            trailing={
              <button
                type="button"
                className="queue__claim"
                onClick={(event) => {
                  stopRowToggle(event);
                  void openTestHarness({ jobId: job.id, testerToken: job.testerToken });
                }}
              >
                Resume to submit
                <ExternalLinkIcon />
              </button>
            }
          />

        ))}
      </ul>
    </section>
  );
}
